// Helpers for schedule autosave: the draft payload shape, change-detection,
// and the single "capture the current draft" entry point shared by the
// autosave hook and the pre-restore safety capture.
//
// Most of this file is pure (testable without React); captureScheduleDraft is
// the one I/O function and owns the "skip an incomplete draft" rule so it can't
// drift between its two call sites.
//
// Autosave serialises the editor's current assignments into the same row shape
// replace_crew_assignments consumes, so an auto snapshot restores through the
// exact same path as a manual one. It does NOT run the crew/stage upsert the
// manual save does — for an existing briefing the ids are already real, and
// restore's crew_assignments_set_stage_id trigger re-derives stage_id from
// stage_name when needed.

export interface DraftAssignmentInput {
  id?: string;
  crew_member_id: string;
  event_date: string;
  artist_name?: string;
  stage_id?: string | null;
  stage_name: string;
  filming_start: string;
  filming_end: string;
  assignment_type: "artist" | "custom";
  subject?: string;
  location?: string;
  notes?: string;
  mirror_excluded?: boolean;
  assist_crew_member_ids?: string[];
}

export interface DraftRow {
  id?: string;
  crew_member_id: string;
  assignment_type: string;
  artist_name: string | null;
  subject: string | null;
  stage_id: string | null;
  stage_name: string;
  event_date: string;
  filming_start: string;
  filming_end: string;
  location: string | null;
  notes: string | null;
  mirror_excluded: boolean;
  assist_crew_member_ids: string[];
}

/** True for a value that looks like a real (v4-ish) UUID. */
export function isUuid(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

export interface DraftBuildResult {
  rows: DraftRow[];
  /**
   * Count of assignments dropped because their crew member isn't persisted
   * yet (temp id). When > 0 the draft is INCOMPLETE and must not be
   * autosnapshotted: restoring it would deactivate those crew's now-saved
   * assignments. The caller skips the snapshot in that case.
   */
  droppedUnsaved: number;
}

/**
 * Serialise editor assignments to draft rows in the shape
 * replace_crew_assignments consumes on restore.
 *
 * - Assignments referencing a not-yet-persisted crew member (non-UUID temp id)
 *   are dropped AND counted (see droppedUnsaved) — they'd FK-crash a restore.
 * - stage_id is deliberately emitted as null: on restore the
 *   crew_assignments_set_stage_id trigger re-derives it from stage_name (it
 *   only fires when null), which avoids replaying a stale stage_id that points
 *   at a briefing_stages row deleted since the draft was taken. This mirrors
 *   the manual save path, which resolves stage_id from the stage name too.
 * - lineup_id is intentionally absent: the set_crew_assignment_lineup_id
 *   trigger re-derives it on restore (same as the manual save path, whose
 *   payload also omits it).
 */
export function buildDraftPayload(assignments: DraftAssignmentInput[]): DraftBuildResult {
  let droppedUnsaved = 0;
  const rows: DraftRow[] = [];
  for (const a of assignments) {
    if (!isUuid(a.crew_member_id)) {
      droppedUnsaved += 1;
      continue;
    }
    rows.push({
      id: a.id || undefined,
      crew_member_id: a.crew_member_id,
      assignment_type: a.assignment_type || (a.artist_name ? "artist" : "custom"),
      artist_name: a.artist_name || null,
      subject: a.subject || null,
      stage_id: null,
      stage_name: a.stage_name,
      event_date: a.event_date,
      filming_start: a.filming_start,
      filming_end: a.filming_end,
      location: a.location || null,
      notes: a.notes || null,
      mirror_excluded: a.mirror_excluded ?? false,
      assist_crew_member_ids: a.assist_crew_member_ids ?? [],
    });
  }
  return { rows, droppedUnsaved };
}

/**
 * Capture the editor's current draft as a kind='auto' snapshot. Single owner
 * of the "an INCOMPLETE draft (unsaved crew) must not be snapshotted" rule —
 * shared by the autosave hook and the pre-restore safety capture so the rule
 * can't drift between them. Returns whether a snapshot was actually written.
 */
export async function captureScheduleDraft(
  invokeFn: (
    name: string,
    opts: { body: Record<string, unknown> },
  ) => Promise<{ error: unknown | null }>,
  briefingId: string,
  assignments: DraftAssignmentInput[],
): Promise<{ captured: boolean }> {
  const { rows, droppedUnsaved } = buildDraftPayload(assignments);
  if (droppedUnsaved > 0) return { captured: false };
  const { error } = await invokeFn("snapshot-schedule-draft", {
    body: { briefing_id: briefingId, rows },
  });
  if (error) throw error;
  return { captured: true };
}

/**
 * Stable fingerprint of a draft payload for change detection — autosave skips
 * a round-trip when nothing changed since the last snapshot. Order-independent
 * (sorted) so a pure reordering of the array doesn't count as a change.
 */
export function draftFingerprint(rows: DraftRow[]): string {
  return rows
    .map((r) =>
      [
        r.id ?? "",
        r.crew_member_id,
        r.assignment_type,
        r.artist_name ?? "",
        r.subject ?? "",
        r.stage_id ?? "",
        r.stage_name,
        r.event_date,
        r.filming_start,
        r.filming_end,
        r.location ?? "",
        r.notes ?? "",
        r.mirror_excluded ? "1" : "0",
        [...r.assist_crew_member_ids].sort().join(","),
      ].join("|"),
    )
    .sort()
    .join("\n");
}
